/**
 * @module features/client-device-management/api/assignmentApi
 * @description Schema-validated API fetchers and TanStack Query hooks for device
 * assignment listing, unassigned inventory lookup, single/batch/CSV assignment,
 * and prompt-driven unassignment (Wipe vs Soft Archive).
 */
import { z } from "zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient, ApiSuccessEnvelope, AppError } from "@/lib/api-client";
import {
  AssignDeviceInput,
  AssignmentSummary,
  AssignmentSummarySchema,
  AvailableDevice,
  AvailableDeviceSchema,
  BatchAssignInput,
  BatchAssignResponse,
  BatchAssignResponseSchema,
  BulkCsvAssignResponse,
  BulkCsvAssignResponseSchema,
  PaginationMeta,
  PaginationMetaSchema,
  UnassignDeviceInput,
  UpdateAssignmentInput,
} from "../types";

const BASE = "/super-admin/client-devices";

export interface AssignmentsQueryParams {
  client_id: string;
  page?: number;
  page_size?: number;
  status?: string;
  device_type?: string;
  search?: string;
}

export interface AvailableDevicesQueryParams {
  search?: string;
  device_type?: string;
}

export interface PaginatedAssignmentsResult {
  items: AssignmentSummary[];
  pagination: PaginationMeta;
}

export const clientDeviceKeys = {
  all: ["client-devices"] as const,
  assignments: (params: AssignmentsQueryParams) =>
    ["client-devices", "assignments", params] as const,
  available: (params: AvailableDevicesQueryParams) =>
    ["client-devices", "available", params] as const,
  configs: (assignmentId: string) =>
    ["client-devices", "configs", assignmentId] as const,
  catalogs: (deviceType?: string) =>
    ["client-devices", "catalogs", deviceType ?? "all"] as const,
  wifiVault: (clientId: string) =>
    ["client-devices", "wifi-vault", clientId] as const,
  deviceWifi: (assignmentId: string) =>
    ["client-devices", "device-wifi", assignmentId] as const,
  wifiAudit: (assignmentId: string) =>
    ["client-devices", "wifi-audit", assignmentId] as const,
  quotas: (clientId: string) =>
    ["client-devices", "quotas", clientId] as const,
  stats: ["client-devices", "stats"] as const,
};

/**
 * Validates raw API response payloads against a Zod schema, converting contract
 * drift into a deterministic AppError rather than allowing runtime undefined crashes.
 */
export function parsePayloadOrThrow<T>(
  schema: z.ZodType<T>,
  data: unknown,
  requestId?: string
): T {
  const parsed = schema.safeParse(data);
  if (!parsed.success) {
    throw new AppError({
      message: "The server returned an unexpected response format.",
      errorCode: "RESPONSE_VALIDATION_ERROR",
      errorDetails:
        "Unable to validate server response payload. Please refresh and try again.",
      requestId,
      status: 502,
      isRetryable: true,
    });
  }
  return parsed.data;
}

/**
 * Fetches paginated device assignments scoped to a specific client organization.
 */
export async function getClientAssignments(
  params: AssignmentsQueryParams
): Promise<PaginatedAssignmentsResult> {
  try {
    const qp = new URLSearchParams();
    qp.set("client_id", params.client_id);
    if (params.page) qp.set("page", String(params.page));
    if (params.page_size) qp.set("page_size", String(params.page_size));
    if (params.status && params.status !== "all") qp.set("status", params.status);
    if (params.device_type && params.device_type !== "all") {
      qp.set("device_type", params.device_type);
    }
    if (params.search?.trim()) qp.set("search", params.search.trim());

    const res = await apiClient.get<
      ApiSuccessEnvelope<unknown> & { pagination?: unknown }
    >(`${BASE}/?${qp.toString()}`);

    const rawData = res.data?.data;
    const rawItems = Array.isArray(rawData)
      ? rawData
      : Array.isArray((rawData as Record<string, unknown> | undefined)?.items)
      ? ((rawData as Record<string, unknown>).items as unknown[])
      : [];

    const items = parsePayloadOrThrow(
      z.array(AssignmentSummarySchema),
      rawItems,
      res.data?.request_id
    );

    const rawPagination =
      res.data?.pagination ??
      (rawData && typeof rawData === "object" && "pagination" in rawData
        ? (rawData as Record<string, unknown>).pagination
        : undefined);

    const pagination = rawPagination
      ? parsePayloadOrThrow(PaginationMetaSchema, rawPagination, res.data?.request_id)
      : {
          page: params.page ?? 1,
          page_size: params.page_size ?? 10,
          total: items.length,
          total_count: items.length,
          total_pages: 1,
          has_next: false,
          has_prev: false,
        };

    return { items, pagination };
  } catch (err: unknown) {
    throw AppError.fromUnknown(err, "Failed to retrieve assigned devices.");
  }
}

/**
 * Fetches unassigned hardware devices from central inventory available for allocation.
 */
export async function getAvailableDevices(
  params: AvailableDevicesQueryParams = {}
): Promise<AvailableDevice[]> {
  try {
    const qp = new URLSearchParams();
    if (params.search?.trim()) qp.set("search", params.search.trim());
    if (params.device_type && params.device_type !== "all") {
      qp.set("device_type", params.device_type);
    }
    const querySuffix = qp.toString() ? `?${qp.toString()}` : "";
    const res = await apiClient.get<ApiSuccessEnvelope<unknown>>(
      `${BASE}/available/${querySuffix}`
    );
    return parsePayloadOrThrow(
      z.array(AvailableDeviceSchema),
      res.data?.data ?? [],
      res.data?.request_id
    );
  } catch (err: unknown) {
    throw AppError.fromUnknown(err, "Failed to load available inventory devices.");
  }
}

/**
 * Assigns a single unassigned hardware device to an active client organization.
 */
export async function assignDeviceToClient(
  input: AssignDeviceInput
): Promise<AssignmentSummary> {
  try {
    const res = await apiClient.post<ApiSuccessEnvelope<unknown>>(`${BASE}/`, input);
    return parsePayloadOrThrow(
      AssignmentSummarySchema,
      res.data?.data,
      res.data?.request_id
    );
  } catch (err: unknown) {
    throw AppError.fromUnknown(err, "Failed to assign device to organization.");
  }
}

/**
 * Assigns multiple selected devices to a client organization with line-item tracking.
 */
export async function batchAssignDevicesToClient(
  input: BatchAssignInput
): Promise<BatchAssignResponse> {
  try {
    const res = await apiClient.post<ApiSuccessEnvelope<unknown>>(
      `${BASE}/batch-assign/`,
      input
    );
    return parsePayloadOrThrow(
      BatchAssignResponseSchema,
      res.data?.data,
      res.data?.request_id
    );
  } catch (err: unknown) {
    throw AppError.fromUnknown(err, "Batch device assignment failed.");
  }
}

/**
 * Uploads a CSV file for bulk device assignment to a client organization.
 */
export async function uploadBulkAssignCsv(
  clientId: string,
  file: File
): Promise<BulkCsvAssignResponse> {
  try {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("client_id", clientId);

    const res = await apiClient.post<ApiSuccessEnvelope<unknown>>(
      `${BASE}/bulk-assign-csv/`,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
      }
    );
    return parsePayloadOrThrow(
      BulkCsvAssignResponseSchema,
      res.data?.data,
      res.data?.request_id
    );
  } catch (err: unknown) {
    throw AppError.fromUnknown(err, "Failed to process bulk assignment CSV.");
  }
}

/**
 * Downloads the pre-formatted CSV template for bulk device assignment.
 */
export async function downloadAssignmentCsvTemplate(): Promise<void> {
  let objectUrl: string | null = null;
  try {
    const res = await apiClient.get<Blob>(`${BASE}/bulk-assign-csv/template/`, {
      responseType: "blob",
    });
    objectUrl = URL.createObjectURL(res.data);
    const anchor = document.createElement("a");
    anchor.href = objectUrl;
    anchor.download = "bulk_device_assignment_template.csv";
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } catch (err: unknown) {
    throw AppError.fromUnknown(err, "Failed to download CSV template.");
  } finally {
    if (objectUrl) {
      URL.revokeObjectURL(objectUrl);
    }
  }
}

/**
 * Updates editable details (friendly name, location, operational notes, and/or config overrides)
 * on an active assigned device.
 */
export async function updateClientAssignment(
  assignmentId: string,
  input: UpdateAssignmentInput
): Promise<AssignmentSummary> {
  try {
    const res = await apiClient.put<ApiSuccessEnvelope<unknown>>(
      `${BASE}/${assignmentId}/`,
      input
    );
    return parsePayloadOrThrow(
      AssignmentSummarySchema,
      res.data?.data,
      res.data?.request_id
    );
  } catch (err: unknown) {
    throw AppError.fromUnknown(err, "Failed to update assigned device details.");
  }
}

/**
 * Unassigns an active device using either Factory Reset (`wipe`) or Soft Archive (`archive`).
 */
export async function unassignDeviceFromClient(
  assignmentId: string,
  input: UnassignDeviceInput
): Promise<AssignmentSummary> {
  try {
    const res = await apiClient.post<ApiSuccessEnvelope<unknown>>(
      `${BASE}/${assignmentId}/unassign/`,
      input
    );
    return parsePayloadOrThrow(
      AssignmentSummarySchema,
      res.data?.data,
      res.data?.request_id
    );
  } catch (err: unknown) {
    throw AppError.fromUnknown(err, "Failed to unassign device.");
  }
}

// ============================================================================
// TanStack Query Hooks
// ============================================================================

export function useOrgAssignments(params: AssignmentsQueryParams) {
  return useQuery({
    queryKey: clientDeviceKeys.assignments(params),
    queryFn: () => getClientAssignments(params),
    enabled: Boolean(params.client_id),
    placeholderData: (previousData) => previousData,
  });
}

export function useAvailableDevices(
  params: AvailableDevicesQueryParams,
  enabled = true
) {
  return useQuery({
    queryKey: clientDeviceKeys.available(params),
    queryFn: () => getAvailableDevices(params),
    enabled,
    staleTime: 10_000,
  });
}

export function useAssignDevice(clientId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Omit<AssignDeviceInput, "client_id">) =>
      assignDeviceToClient({ ...input, client_id: clientId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: clientDeviceKeys.all });
      void queryClient.invalidateQueries({ queryKey: ["devices"] });
    },
  });
}

export function useBatchAssignDevices(clientId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Omit<BatchAssignInput, "client_id">) =>
      batchAssignDevicesToClient({ ...input, client_id: clientId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: clientDeviceKeys.all });
      void queryClient.invalidateQueries({ queryKey: ["devices"] });
    },
  });
}

export function useBulkCsvAssign(clientId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => uploadBulkAssignCsv(clientId, file),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: clientDeviceKeys.all });
      void queryClient.invalidateQueries({ queryKey: ["devices"] });
    },
  });
}

export function useUpdateAssignment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      assignmentId,
      input,
    }: {
      assignmentId: string;
      input: UpdateAssignmentInput;
    }) => updateClientAssignment(assignmentId, input),
    onSuccess: (_, { assignmentId }) => {
      void queryClient.invalidateQueries({
        queryKey: clientDeviceKeys.configs(assignmentId),
      });
      void queryClient.invalidateQueries({ queryKey: clientDeviceKeys.all });
    },
  });
}

export function useUnassignDevice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      assignmentId,
      input,
    }: {
      assignmentId: string;
      input: UnassignDeviceInput;
    }) => unassignDeviceFromClient(assignmentId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: clientDeviceKeys.all });
      void queryClient.invalidateQueries({ queryKey: ["devices"] });
    },
  });
}

